import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Wand2,
  Search,
  Loader2,
  Bot,
  FileText,
  Plus,
  Activity,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Clock,
  Target,
  TrendingUp,
} from 'lucide-react';
import api from '@/services/api';
import { listAgents } from '@/services/evalApi';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContentStoreAgent {
  id: string;
  name: string;
  path: string;
  description?: string;
}

type AgentStatus = 'draft' | 'onboarding' | 'active' | 'paused' | 'retired';

interface AgentIdentity {
  id: string;
  agent_name: string;
  display_name: string;
  description?: string;
  role?: string;
  status: AgentStatus;
  owner_id?: string;
  total_runs: number;
  success_rate: number;
  avg_quality_score: number;
  last_run_at?: string;
  created_at: string;
  updated_at: string;
}

interface CreateAgentData {
  agent_name: string;
  display_name: string;
  description: string;
  role: string;
  owner_id: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'retired', label: 'Retired' },
];

const STATUS_COLORS: Record<AgentStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  onboarding: 'bg-blue-100 text-blue-700 border-blue-300',
  active: 'bg-green-100 text-green-700 border-green-300',
  paused: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  retired: 'bg-red-100 text-red-700 border-red-300',
};

const IDENTITIES_PAGE_SIZE = 12;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPercent(value: number | undefined | null): string {
  if (value == null) return '0%';
  return `${(value * 100).toFixed(1)}%`;
}

function formatScore(value: number | undefined | null): string {
  if (value == null) return '0.0';
  return value.toFixed(1);
}

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: AgentStatus }) {
  const colorClass = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <Badge variant="outline" className={colorClass}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Identity Card
// ---------------------------------------------------------------------------

function IdentityCard({ agent, onClick }: { agent: AgentIdentity; onClick: () => void }) {
  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow duration-200 hover:border-primary/30"
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Bot className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-sm font-semibold truncate">
                {agent.display_name || agent.agent_name}
              </CardTitle>
              {agent.display_name && agent.display_name !== agent.agent_name && (
                <p className="text-xs text-muted-foreground truncate">
                  {agent.agent_name}
                </p>
              )}
            </div>
          </div>
          <StatusBadge status={agent.status} />
        </div>
        {agent.role && (
          <p className="text-xs text-muted-foreground mt-1 truncate">{agent.role}</p>
        )}
      </CardHeader>
      <CardContent className="pb-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <Activity className="h-3 w-3" />
            </div>
            <p className="text-sm font-medium">{agent.total_runs ?? 0}</p>
            <p className="text-[10px] text-muted-foreground">Runs</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <Target className="h-3 w-3" />
            </div>
            <p className="text-sm font-medium">{formatPercent(agent.success_rate)}</p>
            <p className="text-[10px] text-muted-foreground">Success</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
              <TrendingUp className="h-3 w-3" />
            </div>
            <p className="text-sm font-medium">{formatScore(agent.avg_quality_score)}</p>
            <p className="text-[10px] text-muted-foreground">Quality</p>
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-0">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>Last run: {formatDate(agent.last_run_at)}</span>
        </div>
      </CardFooter>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Create Agent Identity Dialog
// ---------------------------------------------------------------------------

interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (agent: AgentIdentity) => void;
  defaultOwnerId: string;
}

function CreateAgentDialog({ open, onOpenChange, onCreated, defaultOwnerId }: CreateDialogProps) {
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState<CreateAgentData>({
    agent_name: '',
    display_name: '',
    description: '',
    role: '',
    owner_id: defaultOwnerId,
  });

  useEffect(() => {
    if (open) {
      setFormData({
        agent_name: '',
        display_name: '',
        description: '',
        role: '',
        owner_id: defaultOwnerId,
      });
    }
  }, [open, defaultOwnerId]);

  const handleCreate = async () => {
    if (!formData.agent_name.trim()) {
      toast({ title: 'Validation Error', description: 'Agent name is required.', variant: 'destructive' });
      return;
    }
    if (!formData.display_name.trim()) {
      toast({ title: 'Validation Error', description: 'Display name is required.', variant: 'destructive' });
      return;
    }

    try {
      setCreating(true);
      const response = await api.post('/agent-identities', {
        agent_name: formData.agent_name.trim(),
        display_name: formData.display_name.trim(),
        description: formData.description.trim() || undefined,
        role: formData.role.trim() || undefined,
        owner_id: formData.owner_id.trim() || undefined,
      });
      toast({
        title: 'Agent Created',
        description: `Agent "${formData.display_name}" has been created successfully.`,
      });
      onCreated(response.data);
      onOpenChange(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create agent identity.';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const updateField = (field: keyof CreateAgentData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Agent Identity</DialogTitle>
          <DialogDescription>
            Define a new agent identity with its core properties. You can configure
            additional settings after creation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="create-agent_name">Agent Name *</Label>
            <Input
              id="create-agent_name"
              placeholder="e.g., research-analyst-v1"
              value={formData.agent_name}
              onChange={(e) => updateField('agent_name', e.target.value)}
              disabled={creating}
            />
            <p className="text-xs text-muted-foreground">
              Unique identifier for this agent. Use lowercase with hyphens.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-display_name">Display Name *</Label>
            <Input
              id="create-display_name"
              placeholder="e.g., Research Analyst"
              value={formData.display_name}
              onChange={(e) => updateField('display_name', e.target.value)}
              disabled={creating}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-description">Description</Label>
            <Textarea
              id="create-description"
              placeholder="Describe what this agent does..."
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={3}
              disabled={creating}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-role">Role</Label>
            <Input
              id="create-role"
              placeholder="e.g., Analyst, Assistant, Reviewer"
              value={formData.role}
              onChange={(e) => updateField('role', e.target.value)}
              disabled={creating}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-owner_id">Owner ID</Label>
            <Input
              id="create-owner_id"
              value={formData.owner_id}
              onChange={(e) => updateField('owner_id', e.target.value)}
              disabled={creating}
            />
            <p className="text-xs text-muted-foreground">
              Pre-filled with your user ID. Change only if assigning to another user.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Agent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Agents Tab (Content Store)
// ---------------------------------------------------------------------------

function AgentsTab() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [agents, setAgents] = useState<ContentStoreAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContentAgents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listAgents();
      setAgents(data);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch agents';
      setError(message);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchContentAgents();
  }, [fetchContentAgents]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={fetchContentAgents} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {agents.length === 0 ? (
        <Card className="p-8 text-center">
          <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No agents found. Add agent definitions to the agents/ directory.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {agents.map((agent) => (
            <Card
              key={agent.id}
              className="cursor-pointer transition-shadow hover:shadow-lg"
              onClick={() => navigate(`/agents/${agent.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Bot className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">{agent.name}</h3>
                </div>
                {agent.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {agent.description}
                  </p>
                )}
                <div className="flex items-center gap-1">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground truncate">{agent.path}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground mb-2">Summary</h2>
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold text-primary">{agents.length}</p>
                <p className="text-sm text-muted-foreground">Total Agents</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-500">
                  {agents.filter((a) => a.description).length}
                </p>
                <p className="text-sm text-muted-foreground">With Descriptions</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-green-500">
                  {new Set(agents.map((a) => a.path.split('/').slice(0, -1).join('/'))).size}
                </p>
                <p className="text-sm text-muted-foreground">Directories</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Identities Tab
// ---------------------------------------------------------------------------

function IdentitiesTab() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [identities, setIdentities] = useState<AgentIdentity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / IDENTITIES_PAGE_SIZE));

  const fetchIdentities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get('/agent-identities', {
        params: {
          limit: IDENTITIES_PAGE_SIZE,
          offset: currentPage * IDENTITIES_PAGE_SIZE,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          search: searchTerm.trim() || undefined,
        },
      });
      const data = res.data;

      if (Array.isArray(data)) {
        setIdentities(data);
        setTotalCount(
          data.length >= IDENTITIES_PAGE_SIZE
            ? (currentPage + 1) * IDENTITIES_PAGE_SIZE + 1
            : currentPage * IDENTITIES_PAGE_SIZE + data.length,
        );
      } else if (data && Array.isArray(data.items)) {
        setIdentities(data.items);
        setTotalCount(data.total ?? data.items.length);
      } else if (data && Array.isArray(data.identities)) {
        setIdentities(data.identities);
        setTotalCount(data.total ?? data.identities.length);
      } else {
        setIdentities([]);
        setTotalCount(0);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch agent identities.';
      setError(message);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, searchTerm, toast]);

  useEffect(() => {
    fetchIdentities();
  }, [fetchIdentities]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [statusFilter, searchTerm]);

  const handleIdentityCreated = (agent: AgentIdentity) => {
    setIdentities((prev) => [agent, ...prev]);
    setTotalCount((prev) => prev + 1);
  };

  const displayPage = currentPage + 1;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search identities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchIdentities} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Identity
        </Button>
        {totalCount > 0 && (
          <span className="text-sm text-muted-foreground ml-auto">
            {totalCount} identit{totalCount !== 1 ? 'ies' : 'y'} found
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Content */}
      {loading && identities.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : identities.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Bot className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No identities found</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Create your first agent identity to get started.'}
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Identity
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {identities.map((identity) => (
            <IdentityCard
              key={identity.id}
              agent={identity}
              onClick={() => navigate(`/agents/identities/${identity.id}`)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 border-t">
          <p className="text-sm text-muted-foreground">
            Page {displayPage} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              disabled={currentPage <= 0 || loading}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={displayPage >= totalPages || loading}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <CreateAgentDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleIdentityCreated}
        defaultOwnerId={user?.id ?? ''}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export function AgentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') === 'identities' ? 'identities' : 'agents';

  const handleTabChange = (value: string) => {
    if (value === 'agents') {
      setSearchParams({});
    } else {
      setSearchParams({ tab: value });
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Agents</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your content-store agents and agent identities.
          </p>
        </div>
        <Button onClick={() => navigate('/agents/wizard')}>
          <Wand2 className="h-4 w-4 mr-2" />
          Create Agent
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-4">
            <TabsTrigger value="agents" className="gap-2">
              <Bot className="h-4 w-4" />
              Agents
            </TabsTrigger>
            <TabsTrigger value="identities" className="gap-2">
              <Activity className="h-4 w-4" />
              Identities
            </TabsTrigger>
          </TabsList>

          <TabsContent value="agents">
            <AgentsTab />
          </TabsContent>

          <TabsContent value="identities">
            <IdentitiesTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default AgentsPage;
